/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { getAmlService } from 'src/core/services/azure/azureMachineLearningService';
import { getAzureService } from 'src/core/services/azure/azureService';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { SwitchingTypes } from 'src/customText/appShell/dataTaggingPage/components/CustomTextAzureMLTab';
import { getCustomTextProjectService } from 'src/customText/core/services/projects/customTextProjectService';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { projectStoreToExportedProjectSwagger } from 'src/customText/modules/handlers/projects/projectMappers';

export type AzureMachineLearningHandlerGroup = ReturnType<typeof createAzureMachineLearningHandlerGroup>;

export const createAzureMachineLearningHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const amlService = getAmlService();
    const ctService = getCustomTextProjectService();
    const { rootStore } = dependencies;

    const getWorkspaceLocation = async (amlProjectPath: string, armToken: string) => {
        const splittedPath = amlProjectPath?.split('/');
        const azureMLWorkspace = splittedPath[splittedPath.indexOf('workspaces') + 1];
        const subscriptionId = splittedPath[splittedPath.indexOf('subscriptions') + 1];
        const resourcegroups = splittedPath[splittedPath.indexOf('resourceGroups') + 1];

        try {
            return await getAzureService().getAzureMLWorkspacesByName({
                id: subscriptionId,
                workspaceName: azureMLWorkspace,
                tenantToken: armToken,
                resourcegroups
            });
        } catch (error) {
            throw error;
        }
    };

    const editLabelingLockedState = async ({ update = false, isLabelingLocked }: { update?: boolean; isLabelingLocked?: boolean }) => {
        const currProject = rootStore.currentCustomTextProjectStore;
        const { amlProjectPath, name: projectName, amlWorkspaceContainerName } = currProject;
        try {
            const amlProjectMetadata = amlService.constructAmlProjectMetadataObject({
                projectRoute: amlProjectPath,
                amlWorkspaceContainerName,
                update,
                isLabelingLocked
            });
            await ctService.linkProjectToAml({ projectName, amlProjectMetadata });
        } catch (error) {
            currProject.setAmlProjectPath(null);
            currProject.setIslabelingLocked(null);
            throw error;
        }
    };

    const createAmlProject = async ({
        amlConnectionInfo,
        dataAssetsName,
        dataStoreName,
        tracker
    }: HandlerParams<{ amlConnectionInfo: AMLConnectionInfo; dataAssetsName: string; dataStoreName: string }>) => {
        const { csAccountStore } = rootStore.azureStore.session;
        const { armToken } = rootStore.azureStore.session.userTokens;
        const currProject = rootStore.currentCustomTextProjectStore;
        const { projectKind, extractionStore, classificationStore, name } = currProject;
        const { projectRoute, workspaceRegion, azureMLWorkspace, projectName } = amlConnectionInfo;

        try {
            tracker.setState('running');

            await amlService.constructAmlDataStoreObject({ amlConnectionInfo, dataStoreName, armToken, region: workspaceRegion });

            const response = await amlService.constructAmlDataAssetsObject({
                amlConnectionInfo,
                dataAssetsName,
                dataStoreName,
                armToken,
                region: workspaceRegion
            });

            const projectResponse = await amlService.constructAmlProjectObject({
                amlConnectionInfo,
                datasetId: response.content.datasetId,
                projectKind,
                extractionStore,
                classificationStore,
                region: workspaceRegion,
                armToken,
                resourceEndPoint: csAccountStore.path,
                languageStudioProjectName: name
            });

            const projectId = projectResponse.content;
            const amlProjectPath = `${projectRoute}/projects/${projectId}`;
            const amlProjectMetadata = amlService.constructAmlProjectMetadataObject({ projectRoute: amlProjectPath });
            await getAmlProjectStatus({ amlProjectPath: amlProjectMetadata.AmlProjectPath, workspaceRegion, tracker });
            currProject.setAmlConnectionInfo({
                azureMLWorkspace,
                projectName,
                workspaceRegion,
                projectId,
                datasetId: response.content.datasetId
            });
            tracker.setState('completed');
        } catch (error) {
            tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    const validateProjectName = async ({
        projectName,
        projectRoute,
        workspaceRegion,
        tracker
    }: HandlerParams<{ projectName: string; projectRoute: string; workspaceRegion: string }>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;

        try {
            tracker.setState('running');

            await amlService.constructValidateProjectNameObject({
                armToken,
                region: workspaceRegion,
                projectName,
                projectRoute
            });
        } catch (error) {
            tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    const getAmlProjectInfo = async ({ tracker }: HandlerParams<{}>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        const currProject = rootStore.currentCustomTextProjectStore;
        const { amlProjectPath, isLabelingLocked } = currProject;
        const splittedPath = amlProjectPath?.split('/');
        const azureMLWorkspace = splittedPath[splittedPath.indexOf('workspaces') + 1];

        try {
            tracker?.setState('running');
            const workspaceLocation = await getWorkspaceLocation(amlProjectPath, armToken);
            const projectResponse = await amlService.constructGetProjectNameObject({
                armToken,
                region: workspaceLocation,
                amlProjectPath
            });
            if (isLabelingLocked && projectResponse.content.state === 'Frozen') {
                await editLabelingLockedState({ update: true, isLabelingLocked: false });
                currProject.setIslabelingLocked(false);
            } else if (!isLabelingLocked && projectResponse.content.state !== 'Frozen') {
                await editLabelingLockedState({ update: true, isLabelingLocked: true });
                currProject.setIslabelingLocked(true);
            }

            const instructionsResponse = await amlService.constructGetInstructionsObject({
                armToken,
                region: workspaceLocation,
                amlProjectPath
            });

            const { name: projectName, id, datasetId, type: labelingTaskType, labelCategories: labelsObject } = projectResponse.content;
            const vendor = projectResponse.content.vendorConfig.vendorName;
            const { detailedInstructionsUri: labelingInstructionUrl, instructions: labelingInstructionText } = instructionsResponse.content;
            const labelNames = [];

            for (const [key] of Object.entries(labelsObject)) {
                labelNames.push(key);
            }
            currProject.setAmlConnectionInfo({
                azureMLWorkspace,
                labelingTaskType,
                vendor,
                projectName,
                labelingInstructionText,
                labelingInstructionUrl,
                projectId: id,
                datasetId,
                labelNames
            });
            tracker?.setState('completed');
        } catch (error) {
            tracker?.setState('failed', <Error>error);
            throw error;
        }
    };

    const terminateAmlConnection = async ({ tracker }: HandlerParams) => {
        const currProject = rootStore.currentCustomTextProjectStore;
        const { name } = currProject;
        const { armToken } = rootStore.azureStore.session.userTokens;
        const { amlProjectPath } = currProject;

        try {
            tracker.setState('running');
            const workspaceLocation = await getWorkspaceLocation(amlProjectPath, armToken);
            await amlService.terminateAmlConnection({ armToken, amlProjectPath, region: workspaceLocation });
            await ctService.terminateAmlConnection({ projectName: name });
            currProject.setAmlProjectPath();
            currProject.setIslabelingLocked(false);
            tracker.setState('completed');
        } catch (error) {
            tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    const importAmlLabels = async ({ tracker, onTrain }: HandlerParams<{ onTrain: () => void }>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        const { amlConnectionInfo, name: projectName, amlProjectPath } = rootStore.currentCustomTextProjectStore;
        try {
            tracker.setState('running');
            const workspaceLocation = await getWorkspaceLocation(amlProjectPath, armToken);

            const importLabelObject = await amlService.constructImportLabelsFromAmlObject({
                region: workspaceLocation,
                armToken,
                amlProjectPath,
                amlConnectionInfo,
                operationHandle: {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    retryStrategy: res => res.data?.[0].content.state !== 'Completed',
                    handleCall: async data => {
                        if (data?.[0].content.state === 'Failed') {
                            throw data?.[0].content.message;
                        }
                    }
                }
            });

            await ctService.importAmlLabels({ projectName, importLabelObject });
            onTrain();
            tracker.setState('completed');
        } catch (error) {
            tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    const getAmlWorkspaceInfo = async ({
        projectRoute,
        workspaceRegion
    }: HandlerParams<{ projectRoute: string; workspaceRegion: string }>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        const currProject = rootStore.currentCustomTextProjectStore;

        try {
            const response = await amlService.constructGetAmlWorkspaceInfo({
                region: workspaceRegion,
                armToken,
                amlProjectPath: projectRoute
            });
            currProject.setAmlWorkspaceContainerName(response?.content?.azureStorageSection?.containerName);
        } catch (error) {
            throw error;
        }
    };

    const switchLabelToAml = async ({ tracker }: HandlerParams) => {
        const currProject = rootStore.currentCustomTextProjectStore;
        const { armToken } = rootStore.azureStore.session.userTokens;
        const { name, amlProjectPath } = currProject;
        const amlProjectMetadata = amlService.constructAmlProjectMetadataObject({
            projectRoute: amlProjectPath,
            update: true,
            isLabelingLocked: true
        });

        const labels = projectStoreToExportedProjectSwagger(currProject);
        try {
            tracker.setState('running');
            const workspaceLocation = await getWorkspaceLocation(amlProjectPath, armToken);
            currProject.setIslabelingLocked(true);
            await amlService.switchLabelToAml({
                region: workspaceLocation,
                armToken,
                amlProjectPath,
                labels: JSON.stringify(labels),
                operationHandle: {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    retryStrategy: res => res.data?.[0].content.state !== 'Completed',
                    handleCall: async data => {
                        switch (data?.[0].content.state) {
                            case 'Failed':
                                throw data?.[0].content.message;
                            case 'Requested':
                                currProject.setSwitchToAmlState(SwitchingTypes.Freezing);
                                break;
                            case 'Importing':
                                currProject.setSwitchToAmlState(SwitchingTypes.Exporting);
                                break;
                            case 'Completed':
                                currProject.setSwitchToAmlState(SwitchingTypes.Stable);
                                break;
                            default:
                        }
                    }
                }
            });
            await ctService.linkProjectToAml({ projectName: name, amlProjectMetadata });
            tracker.setState('completed');
        } catch (error) {
            currProject.setSwitchToAmlState(SwitchingTypes.Stable);
            currProject.setIslabelingLocked(false);
            tracker.setState('failed', error);
            throw error;
        }
    };

    const getAmlProjectStatus = async ({
        amlProjectPath,
        workspaceRegion,
        tracker
    }: HandlerParams<{ amlProjectPath: string; workspaceRegion: string }>) => {
        const { armToken } = rootStore.azureStore.session.userTokens;
        try {
            tracker.setState('running');
            await amlService.getAmlProjectStatus({
                armToken,
                region: workspaceRegion,
                amlProjectPath,
                operationHandle: {
                    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
                    retryStrategy: res => res.data?.[0].content.state !== 'Running',
                    handleCall: async data => {
                        const currProject = rootStore.currentCustomTextProjectStore;
                        if (data?.[0].content.state === 'Running') {
                            currProject.setAmlProjectStatus('Running');
                            currProject.setAmlProjectPath(amlProjectPath);
                            currProject.setIslabelingLocked(false);
                            await editLabelingLockedState({});
                            tracker.setState('completed');
                        } else if (data?.[0].content.state === 'Initializing') {
                            currProject.setAmlProjectStatus('Initializing');
                        }
                        if (data?.[0].content.state === 'Failed') {
                            currProject.setAmlProjectStatus('Failed');
                            currProject.setAmlProjectPath(null);
                            currProject.setIslabelingLocked(null);
                            currProject.setAmlWorkspaceContainerName(null);
                            throw data?.[0].content.statusMessages[0];
                        }
                    }
                }
            });
        } catch (error) {
            tracker.setState('failed', <Error>error);
            throw error;
        }
    };

    return {
        importAmlLabels,
        switchLabelToAml,
        createAmlProject,
        getAmlProjectInfo,
        validateProjectName,
        getAmlProjectStatus,
        getAmlWorkspaceInfo,
        terminateAmlConnection
    };
};
